import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { endOfDay, format, startOfDay } from 'date-fns';
import CashRegisterService from '@api/CashRegister/CashRegisterCtrl';
import CashFlowService from '@api/CashFlows/CashFlowCtrl';
import OrdersService from '@api/Orders/OrderCtrl';
import { OrderStatus } from '@api/Orders/Order';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import Calculator from '@components/Utilities/Calculator';
import Authorization from '@components/Utilities/Authorization';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { setCashRegister } from 'src/shared/store/krispisStore';
import UserService from '@api/Users/UserCtrl';
import { buildBranchCashSummaries, getCashRegisterDate } from '@utils/CashRegisters';

interface ModalCashRegisterProps {
    modalCloseCashRegister: OpenModalHookType;
}

/**
 * Cierre de caja con CORTE CIEGO (2026-08-17, pedido del negocio).
 *
 * Quien cierra cuenta el efectivo y captura el monto **sin ver nunca el
 * esperado**: si lo viera, el conteo dejaría de ser una verificación. El sistema
 * calcula el esperado en ese momento, guarda ambos y la diferencia, y solo el
 * admin la ve (Dashboard → Cajas). Aquí no se muestra ningún importe del sistema.
 *
 * El esperado se calcula sobre la VENTANA de esta caja (de su apertura a ahora),
 * no sobre el día completo: una sucursal puede tener varios turnos.
 *
 * UX: cabe SIN scroll — el teclado a la izquierda y el contexto + PIN a la
 * derecha. Nada de id de caja ni historial de actividad: eso es del admin.
 */
export const ModalCloseCashRegister: React.FunctionComponent<ModalCashRegisterProps> = (props: any) => {
    const { modalCloseCashRegister } = props;
    const { cashRegister } = useSelector((state: RootReducer) => state.krispis);
    const cashRegisterService = useMemo(() => new CashRegisterService(), []);
    const cashFlowService = useMemo(() => new CashFlowService(), []);
    const ordersService = useMemo(() => new OrdersService(), []);
    const userService = useMemo(() => new UserService(), []);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [countedCash, setCountedCash] = useState<number>(0);
    const dispatch = useDispatch();

    /** Esperado de ESTA caja, calculado al vuelo y nunca mostrado en pantalla */
    const getExpectedCashForRegister = async (): Promise<number> => {
        const branchOfficeId = cashRegister?.branchOfficeId;
        if (!branchOfficeId || !cashRegister?.id) return 0;
        const openedAt = getCashRegisterDate(cashRegister.date);
        const start = startOfDay(openedAt);
        const end = endOfDay(openedAt);
        const [registers, flows, orders] = await Promise.all([
            cashRegisterService.getCashRegistersByRange(start, end, branchOfficeId),
            cashFlowService.getCashFlows(start, end, branchOfficeId),
            ordersService.getOrders(OrderStatus.Completed, start, end, branchOfficeId)
        ]);
        const [summary] = buildBranchCashSummaries(
            [{ id: branchOfficeId, name: '' } as any],
            registers,
            flows,
            orders,
            { dayEnd: end }
        );
        const own = summary?.registers.find((item) => item.register.id === cashRegister.id);
        return own?.expectedCash ?? 0;
    };

    const onCloseCashRegister = async () => {
        showLoader();
        try {
            if (cashRegister?.id) {
                const expectedCash = await getExpectedCashForRegister();
                await cashRegisterService.closeCashRegister(cashRegister.id, { countedCash, expectedCash });
                dispatch(setCashRegister(undefined));
                // Neutral a propósito: el resultado del corte lo revisa el admin
                showAlertMessage('Caja cerrada. Tu conteo quedó registrado.');
                await userService.onLogout();
            }
            handleClose();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const handleClose = () => {
        modalCloseCashRegister.toggle();
    };

    useEffect(() => {
        if (modalCloseCashRegister.modalOpen) {
            setIsAuthorized(false);
            setCountedCash(0);
        }
    }, [modalCloseCashRegister.modalOpen]);

    const openedAt = cashRegister ? getCashRegisterDate(cashRegister.date) : undefined;

    return (
        <Dialog
            PaperProps={{ style: { borderRadius: 20 } }}
            open={modalCloseCashRegister.modalOpen}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack>
                        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                            Cerrar caja
                        </Typography>
                        {openedAt && (
                            <Typography variant="caption" color="text.secondary">
                                {`Turno abierto ${format(openedAt, 'HH:mm')}${
                                    cashRegister?.username ? ` · ${cashRegister.username}` : ''
                                }`}
                            </Typography>
                        )}
                    </Stack>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ pb: 1 }}>
                {!cashRegister ? (
                    <Typography textAlign="center" py={2}>
                        La caja aun no a sido abierta
                    </Typography>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={7}>
                            <Stack alignItems="center" spacing={0.5}>
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                    Efectivo contado
                                </Typography>
                                <Calculator onChangeValue={setCountedCash} />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <Stack spacing={2} justifyContent="center" height="100%">
                                <Typography variant="body2" color="text.secondary">
                                    Cuenta el efectivo de la caja y captura el total. El sistema hace la comparación
                                    por su cuenta.
                                </Typography>
                                <Authorization
                                    onAuthorized={() => setIsAuthorized(true)}
                                    onUnauthorized={() => setIsAuthorized(false)}
                                    isCurrentAccount
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Al cerrar caja se cerrará tu sesión.
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    disabled={!isAuthorized || countedCash <= 0}
                    fullWidth
                    color="success"
                    variant="contained"
                    onClick={onCloseCashRegister}
                >
                    Cerrar caja
                </Button>
            </DialogActions>
        </Dialog>
    );
};
